/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import React, {
  useState, useCallback, useEffect,
} from 'react'
import {
  Box, Text, TextInput, Button,
} from '@usap/uicomponents'
import { useShoppingCartContext } from '../../../providers/ShoppingCartProvider'
import useBuyNowCouponController from '../../../../../controllers/buyNowCoupon.controller'

import shoppingCartV2API from '../../../api'
import nrLogger from '../../../../../helpers/nrLogger'
import useWmoEnabled from '../../../../../hooks/useWmoEnabled'
import CouponInputWmo from './CouponInputWmo'
import trackApplyCouponValidationInCart from '../../../logging/gtm/trackApplyCouponValidationInCart'
import StopWatch from '../../../logging/StopWatch'
import trackApplyCouponValidationErrorInCart from '../../../logging/gtm/trackApplyCouponValidationErrorInCart'
import { GTM } from '../../../../../helpers'
import { useLocationContext } from '../../../../../contexts/location.context';
import trackApplyCouponInCart from '../../../logging/gtm/trackApplyCouponInCart'

const CouponInput = props => {
  const { data } = props
  const { modifiers } = data
  const [coupon, setCoupon] = useState('')
  const [couponGtmData, setCouponGtmData] = useState([])
  const locationData = useLocationContext()
  const {
    getCartId,
    getIdentifier,
    shouldShowLoader,
    setCart,
    showLoader,
  } = useShoppingCartContext()

  const { addBuyNowCoupon } = useBuyNowCouponController()

  const isCouponEmpty = coupon.trim() === '' ||
    (modifiers && modifiers.coupon && coupon === modifiers.coupon.code)

  let validMessage = ''
  let isValidCoupon = false
  let hasError = false
  let errorMessages = []
  const errorMessage = ''

  if (modifiers && modifiers.coupon && modifiers.coupon.discounts) {
    const [appliedCoupon] = modifiers.coupon.discounts
      .filter(filterItem => filterItem.applied === true)

    if (appliedCoupon) {
      validMessage = appliedCoupon.response.message
      isValidCoupon = true
    }

    const notAppliedCoupon = (modifiers.coupon.discounts || [])
      .filter(filterItem => filterItem.applied === false)

    if (notAppliedCoupon && notAppliedCoupon.length > 0) {
      hasError = true
      // match pattern $1.00 | $1,000.00
      const regex = /\$((([0-9]+,)|([0-9]))+\.[0-9]+)/i
      errorMessages = notAppliedCoupon
        .map(value => {
          let converted = value.response.message
          const [found] = converted.match(regex) || []
          if (found) {
            converted = converted.replace(found, `<strong class='redTxt'>${found}</strong>`)
          }
          return {
            code: value.response.code,
            message: converted,
          }
        })
    }
  }

  const applyCouponHandler = useCallback(
    async (event, aData) => {
      if (isCouponEmpty || (
        event &&
          aData &&
          aData.coupon &&
          coupon === aData.coupon.code
      )
      ) {
        return false
      }

      shouldShowLoader(true)

      try {
        const identifier = getIdentifier()
        const cartId = getCartId()
        const couponModifier = {
          coupon: {
            code: coupon,
          },
        }

        const timer = new StopWatch()
        timer.start()
        const res = await shoppingCartV2API.coupons.apply(identifier, cartId, couponModifier)
        timer.stop()

        if (isWmoEnabled) {
          trackApplyCouponInCart(res)
        }
        trackApplyCouponValidationInCart(res, timer.getElapsed(), false)

        if (res?.modifiers?.coupon?.discounts) {
          const [couponApplied] = res.modifiers.coupon.discounts
            .filter(discount => discount.applied === true)

          if (couponApplied?.code) {
            addBuyNowCoupon({
              buyNowCouponDB: [couponApplied.code],
            })
            if (window.localStorage) {
              window.localStorage.setItem('coupon', couponApplied.code)
            }
          }
        }

        setCart(res)
      } catch (error) {
        trackApplyCouponValidationErrorInCart(error, false)
        nrLogger.error('ShoppingCartV2.CouponInput.applyCouponHandler', error)
      }

      shouldShowLoader(false)
      return true
    },
    [isCouponEmpty, coupon, shouldShowLoader, getIdentifier, getCartId, setCart, addBuyNowCoupon],
  )

  const enterCouponHandler = event => {
    setCoupon(event.target.value.toUpperCase())
  }

  useEffect(() => {
    if (modifiers?.coupon?.code) {
      setCoupon(modifiers.coupon.code)

      const isExisting = couponGtmData.some(modifier => modifier.coupon.code === modifiers.coupon.code)

      if (!isExisting) {
        const updatedModifiers = {
          ...modifiers,
          location: locationData,
        }

        setCouponGtmData(prev => [...prev, updatedModifiers])
      }
    } else {
      setCoupon('')
    }
  }, [couponGtmData, locationData, modifiers])

  useEffect(() => {
    GTM.cartCouponValidationData(couponGtmData)
  }, [couponGtmData])

  const isWmoEnabled = useWmoEnabled()

  if (isWmoEnabled) {
    return (
      <CouponInputWmo
        applyCouponHandler={applyCouponHandler}
        enterCouponHandler={enterCouponHandler}
        isCouponEmpty={isCouponEmpty}
        errorMessages={errorMessages}
        errorMessage={errorMessage}
        coupon={coupon}
        modifiers={modifiers}
        isValidCoupon={isValidCoupon}
        validMessage={validMessage}
        hasError={hasError}
      />
    )
  }

  return (
    <>
      <Box
        direction="row"
        justify="between"
        background="white"
        pad={{ horizontal: 'small' }}
        border={{ color: 'light-4', size: 'xsmall' }}
        round="xsmall"
      >
        <Box>
          <TextInput
            plain
            dropHeight="medium"
            style={{ fontSize: '14px', fontWeight: 'normal' }}
            background="white"
            value={coupon}
            placeholder="Enter Coupon Code"
            onKeyUp={event => { if (event.key === 'Enter') applyCouponHandler(event) }}
            onChange={event => { enterCouponHandler(event) }}
          />
        </Box>
        <Box align="center" justify="center">
          <Button
            plain
            color="status-critical"
            alignSelf="end"
            margin={{ horizontal: 'small' }}
            style={{ cursor: isCouponEmpty ? 'default' : 'pointer' }}
            disabled={isCouponEmpty || showLoader}
            label={(
              <Text
                size="small"
                color="blueLink"
                style={{ textDecoration: 'none' }}
              >
                Apply
              </Text>
            )}
            onClick={event => applyCouponHandler(event, modifiers)}
          />
        </Box>
      </Box>
      { (isValidCoupon === true) && (
        <Box>
          <Text
            size="xsmall"
            margin={{ horizontal: 'small' }}
            color="neutral-1"
          >
            {validMessage}
          </Text>
        </Box>
      )}
      { (hasError === true && errorMessage.trim() !== '') && (
        <Box>
          <Text
            size="xsmall"
            margin={{ horizontal: 'small' }}
            color="red"
          >
            {errorMessage}
          </Text>
        </Box>
      )}
      { (hasError === true && errorMessages.length > 0) && (
        <ul style={{ margin: 0, padding: '4px 0px 0px 26px' }}>
          {
            errorMessages.map((message, liIndex) => (
              <li key={liIndex.toString()} style={{ fontSize: 'small', lineHeight: '20px' }}>
                <Text
                  size="xsmall"
                  margin={{ bottom: 'small' }}
                  color={message.code === '7001' ? 'red' : 'dark-1'}
                  dangerouslySetInnerHTML={{
                    __html: message.message,
                  }}
                />
              </li>
            ))
          }
        </ul>
      )}
    </>
  )
}

export default CouponInput
